import { formatDate } from '@angular/common';
import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AppSettings } from 'src/app/app-settings';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { EventosService, StateTag } from 'src/app/services/eventos.service';
import { FavoritosService } from 'src/app/services/favoritos.service';
import { environment } from 'src/environments/environment';
import { InfoCardComponent } from '../../shared/info-card/info-card.component';
import { SessionsService } from 'src/app/services/sessions.service';
import { Subscription } from 'rxjs';
import { SalesCountdown, SalesCountdownService } from 'src/app/services/sales-countdown.service';

@Component({
  selector: 'app-card',
  templateUrl: './card.component.html',
  styleUrls: ['./card.component.scss']
})
export class CardComponent implements OnInit, OnDestroy {

  @Input() card: any;
  @Input() list: boolean = false;
  today = new Date();
  user!: any;
  isWishlist: boolean = false;
  urlImages: string = AppSettings.MEDIA_URI + '/events_images/';
  sharedSupported: boolean = false;
  @Output() eliminado = new EventEmitter<any>();
  @Input() teatroIdeal: boolean = false;
  imageUrl: string = '';

  totemActive: any = AppSettings.ENV_TOTEM;
  nextSession: any = null;
  /** Cuenta atrás hasta que abre la venta; null en cuanto ya se puede comprar */
  countdown: SalesCountdown | null = null;
  tag: StateTag | null = null;
  private favoritosSubscription: Subscription | null = null;
  private countdownSubscription: Subscription | null = null;

  constructor(private eventos: EventosService, private favoritosService: FavoritosService, private sessionsService: SessionsService,
    private auth: AuthenticationService, private dialog: MatDialog, private router: Router,
    private salesCountdown: SalesCountdownService) { }

  get displayFromPrice(): number {
    return this.card?.custom_from_price ?? this.card?.from_price;
  }

  // sold_out llega como [] cuando el evento nunca ha pasado por ese estado
  get isSoldOut(): boolean {
    return this.card?.sold_out === true;
  }

  /** La venta todavía no ha abierto. Un evento agotado ya no es "próximamente" */
  get isUpcoming(): boolean {
    return !this.isSoldOut && this.countdown != null;
  }

  get cityName(): string | null {
    return this.card?.city?.name ?? this.card?.space?.city?.name ?? null;
  }

  ngOnInit(): void {
    this.user = this.auth.user;

    // Verificar en el array favoritos (para eventos que vienen con esta info)
    const ob = this.card.favoritos?.findIndex(value => value.id == this.user);
    const inFavoritosArray = ob !== undefined && ob !== -1;

    // También verificar en la lista global de favoritos del servicio
    const inFavoritosService = this.favoritosService.isFavorito(this.card.id);

    // El evento está en favoritos si está en cualquiera de las dos fuentes
    this.isWishlist = inFavoritosArray || inFavoritosService;

    // Suscribirse a cambios en la lista de favoritos para actualizar el estado
    this.favoritosSubscription = this.favoritosService.favoritosIds$.subscribe(() => {
      const ob = this.card.favoritos?.findIndex(value => value.id == this.user);
      const inArray = ob !== undefined && ob !== -1;
      const inService = this.favoritosService.isFavorito(this.card.id);
      this.isWishlist = inArray || inService;
    });

    if (this.card.image_url && this.card.external && this.card.image_url.includes('http')) {
      this.imageUrl = this.card.image_url;
    } else {
      this.imageUrl = this.urlImages + this.card.image_url;
    }
    const navigator = window.navigator as any;
    if (navigator.share) {
      this.sharedSupported = true;
    }
    this.nextSession = this.sessionsService.getNextSession(this.card);
    this.card.nextSession = this.nextSession;

    // Cuenta atrás solo si la venta abre más tarde. Cuando llega a cero el
    // servicio manda null y la card se queda como una normal, sin recargar
    if (this.card.start_sales && !this.isSoldOut) {
      this.countdownSubscription = this.salesCountdown.countdownTo(this.card.start_sales)
        .subscribe(countdown => {
          const wasUpcoming = this.countdown != null;
          this.countdown = countdown;
          if (wasUpcoming !== (countdown != null)) {
            this.refreshTag();
          }
        });
    }
    this.refreshTag();
  }

  /**
   * La etiqueta se calcula una vez y no en un getter: el reloj de la cuenta
   * atrás dispara detección de cambios cada segundo y resolverla reordena las
   * sesiones del evento en cada llamada.
   */
  private refreshTag(): void {
    this.tag = this.eventos.getStateTag(this.card);
  }

  tagString(card: any) {
    return (this.eventos.getTagString(card));
  }

  tagIcon(card: any) {
    return (this.eventos.getTagIcon(card));
  }

  datesEquals(card: any) {
    let fechaCard = card.nextSession?.start_date;
    return (this.eventos.datesEquals(fechaCard));
  }

  shareEvent() {
    if (this.sharedSupported) {
      const shareData = {
        title: this.card.title,
        url: AppSettings.FRONT + '/event/' + this.card.id + '-' + this.card.title.replaceAll(' ', '-')
      };
      navigator.share(shareData).catch((error) => { if (environment.debug) { console.log('Error sharing', error) } });
    }
  }

  editFavorito() {
    if (this.user) {
      const wasWishlist = this.isWishlist;
      // Usar el nuevo endpoint toggle
      this.favoritosService.toggleFavorito(this.card.id).subscribe(res => {
        if (res) {
          // La suscripción a favoritosIds$ se encargará de actualizar isWishlist
          // Solo emitir el evento si se eliminó
          if (wasWishlist) {
            this.eliminado.emit(this.card.id);
          }
        }
      });
    } else {
      this.dialog.open(InfoCardComponent, {
        panelClass: 'info-dialog',
        data: {
          icon: false,
          text: 'Inicia sesión y empieza a guardar tus eventos favoritos',
          btnCancel: false
        }
      });
    }
  }

  addFavorito() {
    // Método mantenido por compatibilidad, ahora usa toggle
    this.editFavorito();
  }

  deleteFavorito() {
    // Método mantenido por compatibilidad, ahora usa toggle
    this.editFavorito();
  }

  goToPage(card: any) {
    let url = '/event';
    const date = card.start_date;
    this.router.navigate([url, card.id + '-' + card.title.replaceAll(' ', '-')]);
  }

  openInNewTab(event: MouseEvent, card: any) {
    event.preventDefault();
    const slug = card.id + '-' + card.title.replaceAll(' ', '-');
    window.open('/event/' + slug, '_blank');
  }

  ngOnDestroy(): void {
    if (this.favoritosSubscription) {
      this.favoritosSubscription.unsubscribe();
    }
    if (this.countdownSubscription) {
      this.countdownSubscription.unsubscribe();
    }
  }

}
